import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useParams } from "react-router-dom";
import useAutomations from "../../../hooks/automationhooks/useAutomations";
import PipeBuilderResults from "../../pipebuilder/PipeBuilderResults";
import PipeBuilder from "../../pipebuilder/PipeBuilder";

export default function EnhancedPipeBuilderMainMenu({
  value,
  onChange,
  queryTypes,
  leadAgeRestriction = null,
}) {
  const COLOR_SURFACE = "#2C2B2D";
  const COLOR_HEADER = "#1E1E1E";
  const COLOR_ACCENT = "#0DA3E2";
  const { brandId } = useParams();
  const [queryResponse, setQueryResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isQueryBuilderExpanded, setIsQueryBuilderExpanded] = useState(true);
  const [isResultsExpanded, setIsResultsExpanded] = useState(false);
  const [joinConfigs, setJoinConfigs] = useState({});

  const {
    handleGetQueryResults,
    handdleExecuteEnhancedQuery,
    handleGetDynamicTableFields,
  } = useAutomations();

  const handlePipeBuilderChange = useCallback(
    (newValue) => {
      onChange(newValue);
    },
    [onChange]
  );

  // Load dynamic tables when component mounts
  useEffect(() => {
    if (brandId) {
      handleGetDynamicTableFields(brandId);
    }
  }, [brandId]);

  useEffect(() => {
    const initial =
      value && value[0] && value[0].joinConfigs ? value[0].joinConfigs : {};
    setJoinConfigs(initial);
  }, [value]);

  const handleRunQuery = useCallback(async () => {
    setIsLoading(true);
    try {
      const effectiveJoinConfigs =
        joinConfigs && Object.keys(joinConfigs).length
          ? joinConfigs
          : value && value[0] && value[0].joinConfigs
          ? value[0].joinConfigs
          : {};

      const result = await handdleExecuteEnhancedQuery(
        brandId,
        value,
        effectiveJoinConfigs,
        leadAgeRestriction
      ); // ADD joinConfigs HERE
      setQueryResponse(result);
      // Expand results and collapse query builder after running query
      setIsResultsExpanded(true);
      setIsQueryBuilderExpanded(false);
    } catch (error) {
      console.error("Error fetching query:", error);
      const message =
        error?.response?.data?.Message ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch query result";
      setQueryResponse({ error: message });
      setIsResultsExpanded(true);
      setIsQueryBuilderExpanded(false);
    } finally {
      setIsLoading(false);
    }
  }, [brandId, value, joinConfigs, leadAgeRestriction, handdleExecuteEnhancedQuery]); // ADD joinConfigs HERE

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        minWidth: 0,
        height: "100%",
        bgcolor: COLOR_SURFACE,
      }}
    >
      {/* PipeBuilder - Left column */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          transition: "flex-basis 0.3s ease, max-width 0.3s ease",
          flex: isQueryBuilderExpanded ? "1 1 72%" : "0 0 48px",
          maxWidth: isQueryBuilderExpanded ? "72%" : "48px",
          minWidth: isQueryBuilderExpanded ? 0 : "48px",
          borderRight: "1px solid rgba(173, 180, 189, 0.2)",
          position: "relative",
          overflow: "hidden",
          bgcolor: COLOR_SURFACE,
        }}
      >
        {!isQueryBuilderExpanded ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "48px",
              height: "100%",
              bgcolor: "background.default",
              cursor: "pointer",
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
            onClick={() => setIsQueryBuilderExpanded(true)}
          >
            <Typography
              sx={{
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                fontWeight: "medium",
                color: "text.secondary",
                userSelect: "none",
                fontSize: "0.875rem",
              }}
            >
              Query Builder
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                p: 2,
                borderBottom: "1px solid rgba(173, 180, 189, 0.2)",
                bgcolor: COLOR_HEADER,
                position: "sticky",
                top: 0,
                left: 0,
                zIndex: 10,
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: "medium", color: COLOR_ACCENT }}
              >
                Query Builder
              </Typography>
            </Box>

            <Box sx={{ flex: 1, overflow: "auto", width: "100%", minWidth: 0 }}>
              <PipeBuilder
                value={value}
                onChange={handlePipeBuilderChange}
                queryTypes={queryTypes}
                onJoinConfigChange={setJoinConfigs}
                leadAgeRestriction={leadAgeRestriction}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* PipeBuilderResults - Right column */}
      <Box
        sx={{
          flex: isQueryBuilderExpanded ? "0 1 28%" : "1 1 auto",
          minWidth: isQueryBuilderExpanded ? 280 : 0,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
            bgcolor: COLOR_SURFACE,
        }}
      >
        {/* Header with title and collapse button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderBottom: "1px solid rgba(173, 180, 189, 0.2)",
            bgcolor: COLOR_HEADER,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "medium", color: COLOR_ACCENT }}>
            Query Results with Join Data
          </Typography>
          {isQueryBuilderExpanded && (
            <IconButton
              onClick={() => setIsQueryBuilderExpanded(false)}
              size="small"
              sx={{
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          )}
          {!isQueryBuilderExpanded && (
            <IconButton
              onClick={() => setIsQueryBuilderExpanded(true)}
              size="small"
              sx={{
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Run Query Button underneath Results */}
        <Box sx={{ p: 2 }}>
          <Button
            variant="contained"
            onClick={handleRunQuery}
            disabled={isLoading || !value || value.length === 0}
            sx={{
              minWidth: "120px",
              bgcolor: COLOR_ACCENT,
              textTransform: "none",
              borderRadius: "7px",
              "&:hover": {
                bgcolor: COLOR_ACCENT,
                opacity: 0.92,
              },
            }}
          >
            {isLoading ? <CircularProgress size={20} /> : "Run Query"}
          </Button>
        </Box>

        {/* Results Content */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <PipeBuilderResults
              value={value}
              result={queryResponse}
              isExpanded={isResultsExpanded}
              setIsExpanded={setIsResultsExpanded}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
